import { AlertTriangle } from "lucide-react";

import { SiteFooter } from "@/components/site-footer";
import { TopBar } from "@/components/top-bar";
import { SITE_URL } from "@/lib/site-url";
import { getLegalDoc, resolveLegalLang, type LegalSlug } from "@/lib/legal";
import { operatorDetailsHidden } from "@/lib/operator";

export const PUBLIC_LOCALES = ["en", "es", "fr", "de", "it", "pt", "zh", "ru"] as const;
export type PublicLocale = (typeof PUBLIC_LOCALES)[number];

export function isPublicLocale(value: string): value is PublicLocale {
  return (PUBLIC_LOCALES as readonly string[]).includes(value);
}

/**
 * Build the `head()` payload for a legal route.
 *
 * Canonical and hreflang are absolute (SEO-003 in the audit: relative canonicals
 * resolve against whatever host served the page and reinforce the wrong domain).
 */
export function legalHead(slug: LegalSlug, lang: string) {
  const doc = getLegalDoc(slug, lang);
  const canonical = `${SITE_URL}/${lang}/${slug}`;
  return {
    meta: [
      { title: doc.metaTitle },
      { name: "description", content: doc.metaDescription },
      // Legal pages should be indexable — payment providers and users look for them —
      // but they are not the pages we want ranking, hence no priority signals.
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: doc.metaTitle },
      { property: "og:description", content: doc.metaDescription },
      { property: "og:type", content: "article" },
      { property: "og:url", content: canonical },
    ],
    links: [
      { rel: "canonical", href: canonical },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/${slug}` },
      ...PUBLIC_LOCALES.map((l) => ({
        rel: "alternate",
        hrefLang: l,
        href: `${SITE_URL}/${l}/${slug}`,
      })),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: doc.title,
          description: doc.metaDescription,
          url: canonical,
          inLanguage: resolveLegalLang(lang),
          dateModified: doc.updated,
        }),
      },
    ],
  };
}

const TRANSLATION_NOTICE: Record<string, string> = {
  fr: "Ce document n'est disponible qu'en anglais et en espagnol. La version anglaise fait foi.",
  de: "Dieses Dokument liegt nur auf Englisch und Spanisch vor. Maßgeblich ist die englische Fassung.",
  it: "Questo documento è disponibile solo in inglese e spagnolo. Fa fede la versione inglese.",
  pt: "Este documento está disponível apenas em inglês e espanhol. A versão inglesa prevalece.",
  ru: "Документ доступен только на английском и испанском языках. Преимущественную силу имеет английская версия.",
  zh: "本文件仅提供英文和西班牙文版本，以英文版为准。",
};

export function LegalPage({ slug, lang }: { slug: LegalSlug; lang: string }) {
  const doc = getLegalDoc(slug, lang);
  const notice = TRANSLATION_NOTICE[lang];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopBar />

      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12">
        {/* Development-only reminder. The operator details are deliberately commented
            out in src/lib/legal.ts for now, and the documents read correctly without
            them — so visitors must not see a warning, but whoever is working on the
            site should still be reminded that the clauses are missing. */}
        {import.meta.env.DEV && operatorDetailsHidden() && (
          <div
            role="status"
            className="mb-8 flex gap-3 rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div>
              <p className="font-semibold">Solo visible en desarrollo</p>
              <p className="mt-1 text-amber-900/90 dark:text-amber-200/80">
                Los datos del operador están comentados en{" "}
                <code className="rounded bg-amber-500/25 px-1 font-mono">src/lib/legal.ts</code>. El
                documento se lee bien sin ellos, pero mientras sigan ocultos no identifica al
                responsable ni fija la ley aplicable. Descomenta las líneas de{" "}
                <code className="rounded bg-amber-500/25 px-1 font-mono">OPERATOR</code> cuando
                quieras activar esas cláusulas.
              </p>
            </div>
          </div>
        )}

        <header>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">{doc.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            <time dateTime={doc.updated}>
              {new Date(doc.updated).toLocaleDateString(lang, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          </p>
          {notice && (
            <p className="mt-4 rounded-lg border border-border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
              {notice}
            </p>
          )}
          <p className="mt-6 text-base leading-relaxed text-foreground/90">{doc.intro}</p>
        </header>

        <div className="mt-10 space-y-9">
          {doc.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold text-foreground">{section.heading}</h2>
              {section.body?.map((paragraph) => (
                <p key={paragraph} className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {paragraph}
                </p>
              ))}
              {section.list && (
                <ul className="mt-3 space-y-2 pl-5">
                  {section.list.map((item) => (
                    <li
                      key={item}
                      className="list-disc text-sm leading-relaxed text-muted-foreground"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
